// =============================================================================
// ui/tabs/CoachLedger.tsx — every rule you have finished with.
// -----------------------------------------------------------------------------
// ⚠ A RETIRED RULE IS NOT A FAILURE, and the blurb says so. It is a hypothesis
// the trader actually tested, which is the only thing that stops them re-running
// the same idea next month with no record that they already had.
//
// ⚠ COLLAPSED, and the summary carries the counts. Once a few rules accumulate,
// an always-open history wedges a wall of text between the loop and whatever
// follows it — but a closed section that says nothing is just a lost feature.
// =============================================================================

import { num } from '../format';
import { leakById, type LedgerRule } from '../../selectors/leaks';

export function CoachLedger({ rows }: { rows: readonly LedgerRule[] }) {
  // ⚠ Rendered only when it has something. An empty "Banked habits" card on a
  // first visit reads as a feature that is broken rather than one not yet used.
  if (!rows.length) return null;

  const banked = rows.filter((r) => r.status === 'banked').length;
  const retired = rows.length - banked;
  const counts = [
    banked ? `${num(banked, 0)} banked` : '',
    retired ? `${num(retired, 0)} retired` : '',
  ].filter(Boolean).join(' · ');

  return (
    <section className="card" data-testid="coach-ledger">
      <details className="coach-marks">
        <summary>
          Banked habits <span className="muted coach-ledger-counts">{counts}</span>
        </summary>
        <p className="muted coach-ledger-blurb">
          Every rule you have proven or retired. A retired rule isn’t a failure —
          it’s a hypothesis you actually tested.
        </p>
        <div className="coach-ledger-rows">
          {rows.map((r, i) => {
            const leak = leakById(r.leakId);
            const when = r.bankedAt || r.retiredAt || r.createdAt;
            return (
              // ⚠ The index is in the key because a ledger entry written by an
              // older client may carry no id at all, and two of those would
              // collide — React drops the second on update, which is how a
              // banked habit would silently vanish from the trader's history.
              <div className="coach-ledger-row" key={`${r.id}-${i}`} data-rule={r.id}>
                <div className="coach-ledger-top">
                  <b className="grow">{r.ruleText || <i className="muted">(no text)</i>}</b>
                  {/* ⚠ No modifier for retired — the base rule IS the neutral
                      look, and a class that duplicates it is dead CSS. */}
                  <span className={`coach-badge${r.status === 'banked' ? ' good' : ''}`}>
                    {r.status === 'banked' ? 'Banked' : 'Retired'}
                  </span>
                </div>
                <div className="muted coach-ledger-meta">
                  {leak ? leak.label : r.leakId} · {r.createdAt || '—'} → {when || '—'}
                </div>
                {r.status === 'retired' && r.reason ? (
                  <div className="muted coach-ledger-why">✕ {r.reason}</div>
                ) : null}
              </div>
            );
          })}
        </div>
      </details>
    </section>
  );
}
